import * as React from "react";
import { cn } from "@/lib/utils";
import { Check, ChevronRight } from "lucide-react";
import { Progress } from "./progress";

interface ChecklistItem {
  id: string;
  label: string;
  completed: boolean;
  onClick?: () => void;
  description?: string;
}

interface ProgressChecklistProps extends React.HTMLAttributes<HTMLDivElement> {
  items: ChecklistItem[];
  completionPercentage?: number;
  showProgress?: boolean;
  title?: string;
}

const ProgressChecklist = React.forwardRef<HTMLDivElement, ProgressChecklistProps>(
  (
    {
      className,
      items,
      completionPercentage,
      showProgress = true,
      title,
      ...props
    },
    ref
  ) => {
    const calculatedPercentage =
      completionPercentage ??
      Math.round((items.filter((i) => i.completed).length / items.length) * 100);

    return (
      <div ref={ref} className={cn("space-y-4", className)} {...props}>
        {title && (
          <h3 className="font-semibold text-[hsl(var(--color-7))]">{title}</h3>
        )}

        {showProgress && (
          <div className="space-y-2">
            <div className="flex items-center justify-between text-sm">
              <span className="text-slate-500">Profile Completion</span>
              <span className="font-semibold text-slate-700">
                {calculatedPercentage}%
              </span>
            </div>
            <Progress value={calculatedPercentage} className="h-2 bg-slate-200 [&>div]:bg-emerald-500" />
          </div>
        )}

        <div className="space-y-1">
          {items.map((item, index) => (
            <div
              key={item.id}
              className={cn(
                "checklist-item group",
                item.onClick && "cursor-pointer hover:bg-slate-200/50 rounded-lg px-2 -mx-2",
                item.completed ? "checklist-item-complete" : "checklist-item-pending"
              )}
              onClick={item.onClick}
              style={{ animationDelay: `${index * 0.05}s` }}
            >
              {item.completed && (
                <div className="flex-shrink-0 w-6 h-6 rounded-full flex items-center justify-center bg-emerald-500 text-white">
                  <Check className="h-4 w-4" />
                </div>
              )}

              <div className="flex-1 min-w-0">
                <p
                  className={cn(
                    "text-sm font-semibold",
                    item.completed
                      ? "text-emerald-700"
                      : "text-slate-900"
                  )}
                >
                  {item.label}
                </p>
                {item.description && (
                  <p className="text-xs text-slate-500">
                    {item.description}
                  </p>
                )}
              </div>

              {item.onClick && !item.completed && (
                <ChevronRight className="h-5 w-5 text-slate-400 group-hover:text-slate-600 transition-colors" />
              )}
            </div>
          ))}
        </div>
      </div>
    );
  }
);
ProgressChecklist.displayName = "ProgressChecklist";

export { ProgressChecklist };
export type { ProgressChecklistProps, ChecklistItem };
